import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  userActivityReportByPortalType,
  userActivityReportUsers,
  appliedFiltersType,
} from "../../../../../services/api/types/reports/reports";
import { formatKey } from "../../utils/helpers";

interface ExportToExcelParams {
  filters: appliedFiltersType;
  reportData: userActivityReportUsers[] | userActivityReportByPortalType[];
  reportName?: string;
  generatedBy?: string;
  time?: string;
  isGroupedByPortal: boolean;
  fileName: string;
}

export const exportToExcel = ({
  filters,
  reportData,
  reportName,
  generatedBy,
  time,
  isGroupedByPortal = false,
  fileName,
}: ExportToExcelParams) => {
  // Flatten the data structure if grouped by portal
  let flattenedData: userActivityReportUsers[] = [];

  if (isGroupedByPortal) {
    const portalData = reportData as userActivityReportByPortalType[];
    portalData.forEach((portal) => {
      portal.users.forEach((activity) => {
        flattenedData.push({
          ...activity,
          portalUid: portal.portalUid,
          portalName: portal.portalName,
          parentPortalUid: portal.parentPortalUid,
        });
      });
    });
  } else {
    flattenedData = reportData as userActivityReportUsers[];
  }

  // Check if user attributes exist in filters
  const hasUserAttributes =
    filters.userAttributes && Object.keys(filters.userAttributes).length > 0;

  // Sheet 1: Filters + Report metadata
  const filterSheetData: any[][] = [
    ["Report type:", reportName || ""],
    ["Generated by:", generatedBy || ""],
    ["Time:", time || ""],
    ["Applied Filters"],
    [
      `Portal: ${filters.general?.Portal || ""}`,
      `Group results by portal: ${filters.general?.GroupByPortal || ""}`,
      `User activity type: ${filters.general?.UserActivity || ""}`,
    ],
  ];

  // Only add User Attributes section if there are user attributes in filters
  if (hasUserAttributes) {
    filterSheetData.push(["User Attributes"]);
    filterSheetData.push(
      Object.entries(filters.userAttributes || {}).map(([key, value]) => [
        `${formatKey(key)}: ${value || ""}`,
      ])
    );
  }

  filterSheetData.push(
    ["Assignments"],
    [
      `Assignment user status: ${filters.assignments?.AssignmentStatus || ""}`,
      `Progress status: ${filters.assignments?.ProgressStatus || ""}`,
    ],
    [
      `Deadline: ${filters.assignments?.DueDate || ""}`,
      `Start date: ${filters.assignments?.StartDate || ""}`,
      `Completion: ${filters.assignments?.EndDate || ""}`,
      `Assign date: ${filters.assignments?.CreatedAt || ""}`,
    ]
  );

  const filterSheet = XLSX.utils.aoa_to_sheet(filterSheetData);

  // Sheet 2: Activity Details
  const activitySheetData = flattenedData.map((activity) => ({
    "Full Name": activity.fullName || "",
    Email: activity.email || "",
    Portal: activity.portalName || activity.activityPortal || "",
    Activity: activity.activity || "",
    "Activity details": activity.activityDetails || "",
    "Activity time stamp": activity.activityTimestamp || "",
  }));

  const activitySheet = XLSX.utils.json_to_sheet(activitySheetData);

  // Create workbook & append both sheets
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, filterSheet, "Info");
  XLSX.utils.book_append_sheet(workbook, activitySheet, "Activity details");

  // Export
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${fileName}.xlsx`);
};
